import { Chart } from './chart';
import { signOf, ChartAspect } from './aspects';
import { CORE_POINTS, SIGN_ELEMENT, SIGN_MODALITY, SIGNS } from '../data/constants';

export interface Stellium {
  kind: 'sign' | 'house';
  sign?: string;
  house?: number;
  planets: string[];
}

export interface AspectPattern {
  type: 'T-Square' | 'Grand Trine' | 'Yod' | 'Grand Cross' | 'Kite';
  planets: string[];
  description: string;
}

export interface ChartPatterns {
  stelliums: Stellium[];
  elements: { Fire: number; Earth: number; Air: number; Water: number; lacking: string[]; dominant: string | null };
  modalities: { Cardinal: number; Fixed: number; Mutable: number; dominant: string | null };
  polarities: { masculine: number; feminine: number };
  aspectPatterns: AspectPattern[];
  chartShape: 'bundle' | 'bowl' | 'bucket' | 'locomotive' | 'seesaw' | 'splash' | 'splay';
  newMoon: boolean;
  fullMoon: boolean;
}

function pairKey(a: string, b: string) {
  return [a, b].sort().join('|');
}

function findAspect(aspects: ChartAspect[], a: string, b: string, name: string): ChartAspect | null {
  return (
    aspects.find(
      (x) => x.name === name && (
        (x.p1 === a && x.p2 === b) || (x.p1 === b && x.p2 === a)
      ),
    ) ?? null
  );
}

function detectAspectPatterns(planets: Record<string, number>, aspects: ChartAspect[]): AspectPattern[] {
  const patterns: AspectPattern[] = [];
  const names = Object.keys(planets).filter((n) => CORE_POINTS.includes(n));
  const seen = new Set<string>();

  // T-Square: two planets in opposition, both square a third.
  for (let i = 0; i < names.length; i++) {
    for (let j = i + 1; j < names.length; j++) {
      const opp = findAspect(aspects, names[i], names[j], 'Opposition');
      if (!opp) continue;
      for (let k = 0; k < names.length; k++) {
        if (k === i || k === j) continue;
        const sq1 = findAspect(aspects, names[i], names[k], 'Square');
        const sq2 = findAspect(aspects, names[j], names[k], 'Square');
        if (sq1 && sq2) {
          const key = 'T:' + [names[i], names[j], names[k]].sort().join(',');
          if (seen.has(key)) continue;
          seen.add(key);
          patterns.push({
            type: 'T-Square',
            planets: [names[i], names[j], names[k]],
            description: `${names[i]}-${names[j]} opposition with ${names[k]} as the apex (focal release point).`,
          });
        }
      }
    }
  }

  // Grand Trine: three planets all in mutual trines.
  for (let i = 0; i < names.length; i++) {
    for (let j = i + 1; j < names.length; j++) {
      const t1 = findAspect(aspects, names[i], names[j], 'Trine');
      if (!t1) continue;
      for (let k = j + 1; k < names.length; k++) {
        const t2 = findAspect(aspects, names[i], names[k], 'Trine');
        const t3 = findAspect(aspects, names[j], names[k], 'Trine');
        if (t2 && t3) {
          const key = 'GT:' + [names[i], names[j], names[k]].sort().join(',');
          if (seen.has(key)) continue;
          seen.add(key);
          patterns.push({
            type: 'Grand Trine',
            planets: [names[i], names[j], names[k]],
            description: `Closed circuit of ease between ${names[i]}, ${names[j]}, and ${names[k]}.`,
          });
        }
      }
    }
  }

  // Yod: two planets in sextile, both quincunx a third.
  for (let i = 0; i < names.length; i++) {
    for (let j = i + 1; j < names.length; j++) {
      const sx = findAspect(aspects, names[i], names[j], 'Sextile');
      if (!sx) continue;
      for (let k = 0; k < names.length; k++) {
        if (k === i || k === j) continue;
        const q1 = findAspect(aspects, names[i], names[k], 'Quincunx');
        const q2 = findAspect(aspects, names[j], names[k], 'Quincunx');
        if (q1 && q2) {
          const key = 'Y:' + [names[i], names[j], names[k]].sort().join(',') + ':' + names[k];
          if (seen.has(key)) continue;
          seen.add(key);
          patterns.push({
            type: 'Yod',
            planets: [names[i], names[j], names[k]],
            description: `Finger of fate pointing at ${names[k]}.`,
          });
        }
      }
    }
  }

  // Grand Cross: two oppositions whose endpoints form four mutual squares.
  for (let i = 0; i < names.length; i++) {
    for (let j = i + 1; j < names.length; j++) {
      const opp1 = findAspect(aspects, names[i], names[j], 'Opposition');
      if (!opp1) continue;
      for (let k = 0; k < names.length; k++) {
        if (k === i || k === j) continue;
        for (let l = k + 1; l < names.length; l++) {
          if (l === i || l === j) continue;
          const opp2 = findAspect(aspects, names[k], names[l], 'Opposition');
          if (!opp2) continue;
          const sqs = [
            findAspect(aspects, names[i], names[k], 'Square'),
            findAspect(aspects, names[i], names[l], 'Square'),
            findAspect(aspects, names[j], names[k], 'Square'),
            findAspect(aspects, names[j], names[l], 'Square'),
          ];
          if (sqs.every(Boolean)) {
            const key = 'GC:' + [names[i], names[j], names[k], names[l]].sort().join(',');
            if (seen.has(key)) continue;
            seen.add(key);
            patterns.push({
              type: 'Grand Cross',
              planets: [names[i], names[j], names[k], names[l]],
              description: `Four-way tension cross binding ${names[i]}, ${names[j]}, ${names[k]}, ${names[l]}.`,
            });
          }
        }
      }
    }
  }

  return patterns;
}

function detectChartShape(planets: Record<string, number>): ChartPatterns['chartShape'] {
  const points = CORE_POINTS
    .map((p) => planets[p])
    .filter((v): v is number => typeof v === 'number')
    .map((v) => ((v % 360) + 360) % 360)
    .sort((a, b) => a - b);
  if (points.length < 7) return 'splash';

  // Compute gaps between consecutive points (wrapping).
  const gaps: { gap: number; index: number }[] = [];
  for (let i = 0; i < points.length; i++) {
    const next = points[(i + 1) % points.length];
    let gap = next - points[i];
    if (i === points.length - 1) gap = 360 - points[i] + points[0];
    gaps.push({ gap, index: i });
  }
  gaps.sort((a, b) => b.gap - a.gap);
  const largestGap = gaps[0].gap;
  const secondGap = gaps[1].gap;

  // Span = 360 - largest gap.
  const span = 360 - largestGap;

  if (span <= 120) return 'bundle';
  if (span <= 180 && largestGap >= 180) return 'bowl';
  if (largestGap >= 120 && secondGap < 60) {
    // Bowl with one isolated planet → bucket.
    return 'bucket';
  }
  if (largestGap >= 60 && largestGap < 120 && span >= 240) return 'locomotive';
  // Two large gaps roughly opposite each other → seesaw.
  if (largestGap >= 60 && secondGap >= 60 && Math.abs(largestGap - secondGap) < 30) return 'seesaw';
  // Roughly even distribution → splash; otherwise splay.
  const allSmall = gaps.every((g) => g.gap < 80);
  return allSmall ? 'splash' : 'splay';
}

export function computeChartPatterns(chart: Chart): ChartPatterns {
  const planets = chart.planets ?? {};
  const placements = chart.placements ?? {};

  // Stelliums by sign.
  const bySign = new Map<string, string[]>();
  const byHouse = new Map<number, string[]>();
  const elements = { Fire: 0, Earth: 0, Air: 0, Water: 0 };
  const modalities = { Cardinal: 0, Fixed: 0, Mutable: 0 };

  for (const name of CORE_POINTS) {
    const lon = planets[name];
    if (typeof lon !== 'number') continue;
    const s = signOf(lon);
    const arr = bySign.get(s.sign) ?? [];
    arr.push(name);
    bySign.set(s.sign, arr);
    elements[SIGN_ELEMENT[s.index] as keyof typeof elements]++;
    modalities[SIGN_MODALITY[s.index] as keyof typeof modalities]++;
    const place = placements[name];
    if (place && typeof place.house === 'number') {
      const arrH = byHouse.get(place.house) ?? [];
      arrH.push(name);
      byHouse.set(place.house, arrH);
    }
  }

  const stelliums: Stellium[] = [];
  for (const [sign, ps] of bySign) {
    if (ps.length >= 3) stelliums.push({ kind: 'sign', sign, planets: ps });
  }
  for (const [house, ps] of byHouse) {
    if (ps.length >= 3) stelliums.push({ kind: 'house', house, planets: ps });
  }

  const elemEntries = Object.entries(elements) as [keyof typeof elements, number][];
  const dominantElement = elemEntries.reduce((a, b) => (b[1] > a[1] ? b : a))[0];
  const lacking = elemEntries.filter(([, n]) => n <= 1).map(([k]) => k);

  const modEntries = Object.entries(modalities) as [keyof typeof modalities, number][];
  const dominantModality = modEntries.reduce((a, b) => (b[1] > a[1] ? b : a))[0];

  const masculine = elements.Fire + elements.Air;
  const feminine = elements.Earth + elements.Water;

  const aspectPatterns = detectAspectPatterns(planets, chart.aspects ?? []);

  // Kite = Grand Trine + a fourth planet opposing one trine vertex (and sextiling the other two).
  const grandTrines = aspectPatterns.filter((p) => p.type === 'Grand Trine');
  const names = Object.keys(planets).filter((n) => CORE_POINTS.includes(n));
  for (const gt of grandTrines) {
    for (const n of names) {
      if (gt.planets.includes(n)) continue;
      const opp = gt.planets.find((p) => findAspect(chart.aspects ?? [], p, n, 'Opposition'));
      if (!opp) continue;
      const others = gt.planets.filter((p) => p !== opp);
      const sx1 = findAspect(chart.aspects ?? [], n, others[0], 'Sextile');
      const sx2 = findAspect(chart.aspects ?? [], n, others[1], 'Sextile');
      if (sx1 && sx2) {
        aspectPatterns.push({
          type: 'Kite',
          planets: [...gt.planets, n],
          description: `Grand Trine + ${n} opposing ${opp} — focused outlet for the trine's gifts.`,
        });
        break;
      }
    }
  }

  // New / Full Moon
  let newMoon = false;
  let fullMoon = false;
  if (typeof planets.Sun === 'number' && typeof planets.Moon === 'number') {
    let diff = Math.abs(planets.Sun - planets.Moon);
    if (diff > 180) diff = 360 - diff;
    newMoon = diff <= 5;
    fullMoon = Math.abs(diff - 180) <= 5;
  }

  return {
    stelliums,
    elements: { ...elements, lacking, dominant: dominantElement },
    modalities: { ...modalities, dominant: dominantModality },
    polarities: { masculine, feminine },
    aspectPatterns,
    chartShape: detectChartShape(planets),
    newMoon,
    fullMoon,
  };
}